import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Divider from "components/ui/divider/Divider";
import EmptyState from "components/ui/empty_state/EmptyState";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { useSsoProviders } from "lib/hooks/api/auth/sso/useSsoProviders";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { CompanyAdminOnlyAlert } from "../CompanyAdminOnlyAlert";
import SsoProviderList from "./SsoProviderList";

function SsoSettingsPage(): JSX.Element {
  const currentUser = useAssertCurrentUser();
  const isCompanyAdmin = currentUser.isCompanyAdmin; // Better-auth doesn't respect stotles admin, just company admin

  const {
    data: providers,
    isLoading: isProvidersLoading,
    isFetching: isProvidersFetching,
    isError: isProvidersError,
  } = useSsoProviders({
    enabled: isCompanyAdmin,
  });

  function renderProvidersContent() {
    if (!isCompanyAdmin) {
      return (
        <CompanyAdminOnlyAlert>
          Only company admins can view and manage SSO providers.
        </CompanyAdminOnlyAlert>
      );
    }
    // Spin while refetching an empty cached list so an in-flight refresh never reads as "none".
    const hasProviders = (providers?.length ?? 0) > 0;
    if (isProvidersLoading || (isProvidersFetching && !hasProviders)) return <CentredSpinner />;
    if (isProvidersError) {
      return (
        <EmptyState
          size="large"
          type="loadError"
          heading="Could not load SSO providers"
          description="There was an error loading your SSO providers. Please try again later."
        />
      );
    }
    return <SsoProviderList providers={providers ?? []} />;
  }

  return (
    <Flex column gap={24}>
      <Flex justifyContent="space-between" alignItems="center">
        <Flex column gap={4}>
          <Text h1>Single sign-on</Text>
          <Text description>Configure SSO for your organisation.</Text>
        </Flex>
      </Flex>

      <Text h2>Shared SSO providers</Text>
      <Flex column gap={16}>
        <Text>
          We provide a shared Microsoft & Google SSO provider for all companies for free. Just
          select the provider you want to use on the login/sign-up page.
        </Text>
        <Text>
          Coming soon: Ability to enforce a shared SSO provider for users in your company.
        </Text>
      </Flex>

      <Divider />

      <Text h2>Custom SSO providers</Text>
      <Flex column gap={16}>
        <Text>We support OpenID Connect (OIDC) and SAML providers for custom SSO integration.</Text>
        {renderProvidersContent()}

        {/* TODO: Maybe only allow 1 SSO provider per company? I'm in two minds since they might need a separate
            provider for different domains but all be in the same company. For now, allow multiple since this helps testing */}
        <Button
          buttonType="secondary"
          as="a"
          to="/account-management/sso/add"
          disabled={!isCompanyAdmin || isProvidersLoading}
          width="fit-content"
        >
          Add SSO provider
        </Button>
      </Flex>
    </Flex>
  );
}

export default SsoSettingsPage;
